import { apiSlice } from "./apiSlice";
import {
  appendNotifications,
  markAllAsRead,
  setNotificationPreferences,
  setNotifications,
  setNotificationsHasMore,
  setUnreadCount,
  updateNotification,
  updateNotificationPreferences,
} from "../slice/miscSlice";

/** First page fills the taller inbox panel (~6–8 rows) with scroll headroom. */
const DEFAULT_PAGE_SIZE = 30;

/**
 * Normalize list responses that may be a bare array (legacy) or paginated.
 *
 * @param {*} data
 * @return {{results: Array, hasMore: boolean, unreadCount: (number|undefined)}}
 */
function normalizeNotificationList(data) {
  if (Array.isArray(data)) {
    return { results: data, hasMore: false, unreadCount: undefined };
  }
  const results = Array.isArray(data?.results) ? data.results : [];
  const hasMore = Boolean(data?.next);
  const unreadRaw = data?.unread_count;
  const unreadCount = unreadRaw === undefined || unreadRaw === null ? undefined : Number(unreadRaw);
  return {
    results,
    hasMore,
    unreadCount: Number.isFinite(unreadCount) ? unreadCount : undefined,
  };
}

export const notificationApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    createNotificationPreferenceGroup: builder.mutation({
      query: data => ({ body: data, method: "POST", url: "/api_ntf/preference_mutate/" }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(updateNotificationPreferences(result.data)))
          .catch(() => {});
      },
    }),
    dismissTasks: builder.mutation({
      query: taskIds => ({
        body: { task_ids: Array.isArray(taskIds) ? taskIds : [taskIds] },
        method: "POST",
        url: "/api_ntf/active-tasks/dismiss/",
      }),
    }),
    getActiveTasks: builder.query({
      query: () => "/api_ntf/active-tasks/",
    }),
    getShareRecipients: builder.query({
      query: ({ q = "", limit = 20, document_type, document_id } = {}) => {
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        params.set("limit", String(limit));
        if (document_type) params.set("document_type", document_type);
        if (document_id) params.set("document_id", document_id);
        return `/api_ntf/share_recipients/?${params.toString()}`;
      },
    }),
    getNotificationPreferences: builder.query({
      query: () => "/api_ntf/preference/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setNotificationPreferences(result.data)))
          .catch(() => {});
      },
    }),
    getNotifications: builder.query({
      query: ({ limit = DEFAULT_PAGE_SIZE, offset = 0 } = {}) =>
        `/api_ntf/user/?limit=${limit}&offset=${offset}`,
      // One cache entry; inbox rows live in miscSlice. Lazy load-more always fetches.
      serializeQueryArgs: ({ endpointName }) => endpointName,
      merge: () => {},
      // Never force page-0 remounts (bell / route changes). Only page > 0 needs a new request.
      forceRefetch: ({ currentArg, previousArg }) => {
        const currentOffset = currentArg?.offset || 0;
        if (currentOffset === 0) {
          return false;
        }
        return (
          currentOffset !== (previousArg?.offset || 0) ||
          (currentArg?.limit || DEFAULT_PAGE_SIZE) !== (previousArg?.limit || DEFAULT_PAGE_SIZE)
        );
      },
      onQueryStarted: async (arg, { dispatch, getState, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const { results, hasMore, unreadCount } = normalizeNotificationList(data);
          const offset = arg?.offset || 0;
          const limit = arg?.limit || DEFAULT_PAGE_SIZE;
          const fullPage = results.length >= limit;

          if (offset > 0) {
            const lengthBefore = getState().misc.notifications.length;
            dispatch(appendNotifications(results));
            const added = getState().misc.notifications.length - lengthBefore;
            // Stop when API says so, page is short, or nothing new was appended (dupes / end).
            dispatch(setNotificationsHasMore(Boolean(hasMore) && fullPage && added > 0));
          } else {
            dispatch(setNotifications(results));
            dispatch(setNotificationsHasMore(Boolean(hasMore) && fullPage));
          }
          // Badge comes from the list payload — no separate unread_count request.
          if (unreadCount !== undefined) {
            dispatch(setUnreadCount(unreadCount));
          }
        } catch (error) {
          // Leave existing inbox state on failure.
        }
      },
    }),
    getUnreadNotificationCount: builder.query({
      // Kept for older clients; app inbox uses unread_count on the list response.
      query: () => "/api_ntf/user/unread_count/",
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(setUnreadCount(data?.count ?? 0));
        } catch (error) {
          // Keep last known count.
        }
      },
    }),
    markAllNotificationsAsRead: builder.mutation({
      query: () => ({ body: {}, method: "PATCH", url: `/api_ntf/user/mark_all_as_read/` }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(() => dispatch(markAllAsRead())).catch(() => {});
      },
    }),
    mutateNotificationPreferenceGroup: builder.mutation({
      query: data => ({
        body: data,
        method: "POST",
        url: "/api_ntf/preference_mutate/create_forge/",
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(updateNotificationPreferences(result.data)))
          .catch(() => {});
      },
    }),
    patchNotificationToIsread: builder.mutation({
      query: ({ id, ...data }) => ({ body: data, method: "PATCH", url: `/api_ntf/user/${id}/` }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(updateNotification(result.data))).catch(() => {});
      },
    }),
    triggerSync: builder.mutation({
      query: data => ({
        body: data,
        method: "POST",
        url: "/api_integration/ext_sys/trigger_sync/",
      }),
    }),
    triggerTestTask: builder.mutation({
      query: data => ({
        body: data,
        method: "POST",
        url: "/api_ntf/test_task/trigger/",
      }),
    }),
    updateNotificationPreferenceGroup: builder.mutation({
      query: ({ id, ...data }) => ({
        body: data,
        method: "PATCH",
        url: `/api_ntf/preference_mutate/${id}/`,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(updateNotificationPreferences(result.data)))
          .catch(() => {});
      },
    }),
  }),
});

export const {
  useCreateNotificationPreferenceGroupMutation,
  useDismissTasksMutation,
  useGetActiveTasksQuery,
  useLazyGetShareRecipientsQuery,
  useGetNotificationPreferencesQuery,
  useGetNotificationsQuery,
  useLazyGetNotificationsQuery,
  useGetUnreadNotificationCountQuery,
  useLazyGetUnreadNotificationCountQuery,
  useMarkAllNotificationsAsReadMutation,
  useMutateNotificationPreferenceGroupMutation,
  usePatchNotificationToIsreadMutation,
  useTriggerSyncMutation,
  useTriggerTestTaskMutation,
  useUpdateNotificationPreferenceGroupMutation,
} = notificationApiSlice;

export { DEFAULT_PAGE_SIZE };
